import { Invitee, InviteeForm, StripeOrgAdminForm } from "@closerplatform/spinner-openapi";
import { addDays } from "date-fns";
import { FieldState, FormState } from "formstate";
import { observable } from "mobx";
import { inject, observer } from "mobx-react";
import React from "react";
import { AgentInput } from "@fidget/common/components/form/agentInput";
import { L10n, L10nHtml } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { salesTaxService } from "@fidget/common/services/salesTaxService";
import { stripeService } from "@fidget/common/services/stripeService";
import { logger, parseQueryString } from "@fidget/common/utils";
import { extractNamesFromEmail } from "@fidget/common/utils/extractNamesFromEmail";
import { scrollToSection } from "@fidget/common/utils/scrollToSection";
import { unwrapFormState } from "@fidget/common/utils/unwrapFormState";
import {
  PasswordValidation,
  validatePasswordPolicy
} from "@fidget/common/utils/validatePasswordPolicy";
import {
  validateEmail,
  validatePassword,
  validateRequired,
  validateUrl
} from "@fidget/common/utils/validatorsFormState";
import { ICurrentUserStore } from "../../../models/currentUser/CurrentUserStore";
import { IPaymentStore } from "../../../models/payments/PaymentStore";
import { IWidgetStore } from "../../../models/widget/WidgetStore";
import { getGtmService } from "../../../services/gtmService";
import { PasswordRules } from "../../passwordRules";
import { ProgressDots } from "../../progressDots/progressDots";
import { BackLinkContainer } from "../components/backLinkContainer";
import { BeforeLoginSection } from "../components/beforeLoginSection";
import { AddAgents, AgentWithID } from "./addAgents";
import { CountryAutocomplete } from "./countryAutocomplete";
import { ErrorSection } from "./errorSection";
import { AgentFormFields } from "./inviteAgentRow";
import { PaymentSummary } from "./paymentSummary";
import { SetUpSection } from "./setUpSection";
import styles from "./signUpCommon.css";
import { UserDetails } from "@fidget/common/models/user-details";
import { Country, countryService } from "@fidget/common/services/countries";

type PasswordFormFields = {
  password: FieldState<string>;
  passwordRepeated: FieldState<string>;
};
type CompanyFormFields = {
  orgName: FieldState<string>;
};
type WebsiteFormFields = {
  website: FieldState<string>;
};
type NameFormFields = {
  firstName: FieldState<string>;
  lastName: FieldState<string>;
};
type PaymentFormFields = {
  country: FieldState<Partial<Country>>;
  companyID: FieldState<string>;
};

type NextFormFields =
  | PasswordFormFields
  | CompanyFormFields
  | WebsiteFormFields
  | NameFormFields
  | Array<FormState<AgentFormFields>>
  | PaymentFormFields;

enum GTMTag {
  PASSWORD = "accelerate-account-password-success",
  BRAND = "accelerate-account-brand-success",
  WEBSITE = "accelerate-account-website-success",
  NAME = "accelerate-account-name-success",
  ADVISERS = "accelerate-account-advisers-success",
  VATID = "accelerate-account-vatid-success"
}

interface Props {
  currentUserStore?: ICurrentUserStore;
  widgetStore?: IWidgetStore;
  paymentStore?: IPaymentStore;
  timeout: number;
  trialDuration: number;
  currency: string;
  price: number;
}

interface State {
  displayMode: "completingAccount" | "error" | "setUp";
  passwordValidation: PasswordValidation;
}

@inject("currentUserStore", "widgetStore", "paymentStore")
@observer
export class AdminSignUpStripe extends React.Component<Props, State> {
  token: string;
  email: string;
  passwordFields: PasswordFormFields;
  passwordForm: FormState<PasswordFormFields>;
  companyFields: CompanyFormFields;
  companyForm: FormState<CompanyFormFields>;
  websiteFields: WebsiteFormFields;
  websiteForm: FormState<WebsiteFormFields>;
  nameFields: NameFormFields;
  nameForm: FormState<NameFormFields>;
  @observable agentsFields: Array<AgentWithID>;
  @observable agentsForm?: FormState<Array<FormState<AgentFormFields>>>;
  @observable paymentFields?: PaymentFormFields;
  paymentForm?: FormState<PaymentFormFields>;
  focusedSectionNumber: number;
  onPasswordFormSubmit: (form: React.FormEvent<Element>) => Promise<void>;
  onNameFormSubmit: (form: React.FormEvent<Element>) => Promise<void>;
  onCompanyFormSubmit: (form: React.FormEvent<Element>) => Promise<void>;
  onWebsiteFormSubmit: (form: React.FormEvent<Element>) => Promise<void>;
  totalSteps = 7;
  styleType: "signup" = "signup";
  ipAddress?: string;
  private gtmService = getGtmService();

  constructor(props: Props) {
    super(props);
    this.state = {
      displayMode: "completingAccount",
      passwordValidation: {
        hasOneNumber: false,
        hasUppercase: false,
        longEnough: false
      }
    };

    const { email, token } = parseQueryString();
    this.token = token || "";
    this.email = email || "";
    const { companyName, companyWebsite } = extractNamesFromEmail(this.email);
    const companyNameInit = (companyName || "").slice(0, 50);

    this.passwordFields = {
      password: new FieldState("")
        .disableAutoValidation()
        .validators(validateRequired, validatePassword),
      passwordRepeated: new FieldState("")
        .disableAutoValidation()
        .validators(validateRequired, this.validatePasswordRepeated)
    };
    this.passwordForm = new FormState(this.passwordFields);

    this.companyFields = {
      orgName: new FieldState(companyNameInit).disableAutoValidation().validators(validateRequired)
    };
    this.companyForm = new FormState(this.companyFields);

    this.websiteFields = {
      website: new FieldState(companyWebsite)
        .disableAutoValidation()
        .validators(validateRequired, validateUrl)
    };
    this.websiteForm = new FormState(this.websiteFields);

    this.nameFields = {
      firstName: new FieldState("").disableAutoValidation().validators(validateRequired),
      lastName: new FieldState("").disableAutoValidation().validators(validateRequired)
    };
    this.nameForm = new FormState(this.nameFields);

    this.agentsFields = [];
    this.addAgent();

    this.focusedSectionNumber = 0;

    this.onPasswordFormSubmit = this._toNextFormSubmit(this.passwordForm, GTMTag.PASSWORD);
    this.onCompanyFormSubmit = this._toNextFormSubmit(this.companyForm, GTMTag.BRAND, {
      additionalData: { "Company name": this.companyFields.orgName.value }
    });
    this.onWebsiteFormSubmit = this._toNextFormSubmit(this.websiteForm, GTMTag.WEBSITE, {
      additionalData: { "Company URL": this.websiteFields.website.value }
    });
    this.onNameFormSubmit = this._toNextFormSubmit(this.nameForm, GTMTag.NAME, {
      firstName: this.nameFields.firstName.value,
      lastName: this.nameFields.lastName.value
    });
  }

  async componentDidMount() {
    const country: Partial<Country> = {};
    try {
      const { country: iso, ip } = await (await fetch("https://get.geojs.io/v1/ip/country.json")).json();
      Object.assign(country, countryService.countryByIso(iso));
      this.ipAddress = ip;
    } catch (error) {
      logger.error({ message: "Couldn't get fetch", error });
    }
    this.paymentFields = {
      country: new FieldState(country).validators(this.validateCountry),
      companyID: new FieldState("").validators(this.validateIDCompany)
    };
    this.paymentForm = new FormState(this.paymentFields);
  }

  validatePasswordRepeated = (val: string) => {
    const { password } = this.passwordFields;
    return (
      !password.hasError &&
      val !== password.value &&
      intl.formatMessage("passwordValidation.passMismatch")
    );
  };

  validateIDCompany = async (val?: string) => {
    const { country: { $: country } } = this.paymentFields!;
    if (country?.iso && val) {
      const isIDvalid = await salesTaxService.validateTaxNumber(country.iso, val);
      return isIDvalid ? "" : intl.formatMessage("signup.payment.placeholder.VAT.error");
    }
    return "";
  };

  validateCountry = async (val?: Partial<Country>) =>
    val?.iso ? "" : "No such country";

  handlePasswordChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ passwordValidation: validatePasswordPolicy(ev.target.value) });
  };

  back = () => scrollToSection(--this.focusedSectionNumber, true);

  _toNextFormSubmit = <T extends NextFormFields>(
    form: FormState<T>,
    tagManagerData: string,
    userDetails?: UserDetails
  ) => {
    return async (e: React.FormEvent) => {
      e.preventDefault();
      const result = await form.validate();
      if (!result.hasError) {
        scrollToSection(++this.focusedSectionNumber);
        if (userDetails) {
          this.props.widgetStore!.updateUserDetails(userDetails);
        }
        this.gtmService.pushTag(tagManagerData);
      }
    };
  };

  validateRequiredEmailIfNamesProvided = ({ firstName, lastName, email }: AgentFormFields) => {
    if ((!!firstName.value || !!lastName.value) && !email.value) {
      email.setError(intl.formatMessage("error.fieldRequired"));
      return intl.formatMessage("error.fieldRequired");
    }
    return "";
  };

  addAgent = () => {
    const agentFields = {
      firstName: new FieldState(""),
      lastName: new FieldState(""),
      email: new FieldState("").disableAutoValidation().validators(validateEmail)
    };

    this.agentsFields.push({
      form: new FormState(agentFields).validators(this.validateRequiredEmailIfNamesProvided),
      id: `${Math.random() * 1000}-${Math.random() * 1000}`
    });
  };

  removeAgent = (id: string) => {
    return () => (this.agentsFields = this.agentsFields.filter((agent) => agent.id !== id));
  };

  validateEmailsUnique = (form: Array<FormState<AgentFormFields>>) => {
    const emailsFields = form.map((item) => item.$.email);
    let formError = "";
    emailsFields.forEach((email1, i) => {
      const isTheSameIndex =
        emailsFields.findIndex((email2) => email1.value === email2.value) === i;
      if (!isTheSameIndex) {
        email1.setError(intl.formatMessage("settings.advisers.error.emailNonUnique"));
        formError = intl.formatMessage("settings.advisers.error.emailNonUnique");
      }
    });
    return formError;
  };

  onInviteAgentsFormSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const fieldsForForm = this.agentsFields.filter(
      (field) => field.form.$.email.$ || field.form.$.lastName.$ || field.form.$.firstName.$
    );
    this.agentsForm = new FormState(fieldsForForm.map((item) => item.form)).validators(
      this.validateEmailsUnique
    );
    await this._toNextFormSubmit(this.agentsForm, GTMTag.ADVISERS)(ev);
  };

  toPaymentSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const resultPaymentForm = await this.paymentForm!.validate();
    if (!resultPaymentForm.hasError) {
      const agents = this.agentsForm
        ? this.agentsForm.$.map((agent) => unwrapFormState<InviteeForm>(agent.$))
        : [];
      const formResult = {
        password: this.passwordFields.password.$,
        customer: { ...unwrapFormState(this.nameForm.$), email: this.email } as Invitee,
        companyDetails: {
          name: this.companyFields.orgName.$,
          ...(unwrapFormState(this.paymentForm!.$) as any)
        },
        website: this.websiteFields.website.$,
        agents,
        tax: this.props.paymentStore!.taxRate
      };

      const createOrgForm: StripeOrgAdminForm = {
        orgName: formResult.companyDetails.name,
        firstName: formResult.customer.firstName || "",
        lastName: formResult.customer.lastName || "",
        password: formResult.password,
        baseUrl: formResult.website,
        vatId: formResult.companyDetails.companyID,
        country: formResult.companyDetails.country.iso,
        ipAddress: this.ipAddress,
        invitees: formResult.agents
      };

      try {
        const { currentUserStore } = this.props;
        logger.info("Creating org", createOrgForm);
        const { sessionId } = await currentUserStore!.createStripeOrgAdmin(
          this.token,
          createOrgForm
        );
        this.gtmService.pushTag(GTMTag.VATID);
        this.setState({ displayMode: "setUp" });
        await stripeService.load();
        await stripeService.redirectToCheckoutWithSessionID(sessionId);
      } catch (error) {
        logger.error({ message: "Couldn't create org", error });
        this.setState({ displayMode: "error" });
      }
    }
  };

  renderBack = () => <BackLinkContainer onClick={this.back} />;

  renderCompletingAccount = () => {
    const { firstName, lastName } = this.nameFields;
    const { password, passwordRepeated } = this.passwordFields;
    const { orgName } = this.companyFields;
    const { website } = this.websiteFields;
    const renewDate = addDays(new Date(), Number(this.props.trialDuration));
    const renewDateFormatted = intl.formatDate(renewDate, {
      day: "numeric",
      month: "long",
      year: "numeric"
    });

    return (
      <>
        <BeforeLoginSection styleType={this.styleType}>
          <ProgressDots fulfilledSteps={1} totalSteps={this.totalSteps} />
          <header>
            <h2>{intl.formatMessage("signup.setYourPassword")}</h2>
            <PasswordRules passwordValidation={this.state.passwordValidation} okColor={"#107377"} />
          </header>
          <form onSubmit={this.onPasswordFormSubmit} id="free-account-password">
            <AgentInput
              field={password}
              type={"password"}
              styleType={this.styleType}
              onChange={this.handlePasswordChange}
              autoFocus
              placeholder={intl.formatMessage("signup.password.placeholder.password")}
              autoComplete={"new-password"}
            />
            <AgentInput
              field={passwordRepeated}
              type={"password"}
              styleType={this.styleType}
              placeholder={intl.formatMessage("signup.password.placeholder.repeatPassword")}
              autoComplete={"new-password"}
            />
            <div className={styles.pushdown}>
              <p>
                <L10nHtml id={"signup.password.youEmail"} values={{ email: this.email }} />
              </p>
              <button type="submit" disabled={!(password.value && passwordRepeated.value)}>
                <L10n id={"signup.continue"} />
              </button>
            </div>
          </form>
        </BeforeLoginSection>
        <BeforeLoginSection styleType={this.styleType}>
          <ProgressDots fulfilledSteps={2} totalSteps={this.totalSteps} />
          <header>
            <h2>
              <L10n id={"signup.yourBrandName"} />
            </h2>
            <div>
              <p>
                <L10n id={"signup.yourBrandName.description"} />
              </p>
            </div>
          </header>
          <form onSubmit={this.onCompanyFormSubmit} id="free-account-brand">
            <AgentInput
              field={orgName}
              styleType={this.styleType}
              maxLength={50}
              placeholder={intl.formatMessage("signup.yourBrandName.placeholder")}
            />
            <div className={styles.pushdown}>
              <p>
                <L10nHtml id={"signup.password.youEmail"} values={{ email: this.email }} />
              </p>
              <button type="submit" disabled={!orgName.value}>
                <L10n id={"signup.continue"} />
              </button>
            </div>
          </form>
          {this.renderBack()}
        </BeforeLoginSection>
        <BeforeLoginSection styleType={this.styleType}>
          <ProgressDots fulfilledSteps={3} totalSteps={this.totalSteps} />
          <header>
            <h2>
              <L10n id={"signup.website.header"} />
            </h2>
            <div>
              <p>
                <L10nHtml id={"signup.website.description"} />
              </p>
            </div>
          </header>
          <form onSubmit={this.onWebsiteFormSubmit} id="free-account-website">
            <AgentInput
              field={website}
              maxLength={120}
              styleType={this.styleType}
              placeholder={intl.formatMessage("signup.website.placeholder")}
            />
            <div className={styles.pushdown}>
              <p>
                <L10nHtml id={"signup.password.youEmail"} values={{ email: this.email }} />
              </p>
              <button type="submit" disabled={!website.value}>
                <L10n id={"signup.continue"} />
              </button>
            </div>
          </form>
          {this.renderBack()}
        </BeforeLoginSection>
        <BeforeLoginSection styleType={this.styleType}>
          <ProgressDots fulfilledSteps={4} totalSteps={this.totalSteps} />
          <header>
            <h2>
              <L10n id={"signup.name.neatHowAddress"} />
            </h2>
            <div>
              <p>
                <L10n id={"signup.name.neatHowAddress.description"} />
              </p>
            </div>
          </header>
          <form onSubmit={this.onNameFormSubmit} id="free-account-name">
            <AgentInput
              field={firstName}
              styleType={this.styleType}
              maxLength={64}
              placeholder={intl.formatMessage("signup.name.placeholder.whatsName")}
            />
            <AgentInput
              field={lastName}
              styleType={this.styleType}
              maxLength={64}
              placeholder={intl.formatMessage("signup.name.placeholder.whatsLastName")}
            />
            <div className={styles.pushdown}>
              <p>
                {" "}
                <L10nHtml id={"signup.password.youEmail"} values={{ email: this.email }} />
              </p>
              <button type="submit" disabled={!(firstName.value && lastName.value)}>
                <L10n id={"signup.continue"} />
              </button>
            </div>
          </form>
          {this.renderBack()}
        </BeforeLoginSection>
        <BeforeLoginSection styleType={this.styleType}>
          <ProgressDots fulfilledSteps={5} totalSteps={this.totalSteps} />
          <header>
            <h2>
              <L10n id={"signup.agents.invite"} />
            </h2>
            <div>
              <p>
                <L10n id={"signup.agents.description"} />
              </p>
            </div>
          </header>
          <form onSubmit={this.onInviteAgentsFormSubmit}>
            <AddAgents
              agentsFields={this.agentsFields}
              removeAgent={this.removeAgent}
              addAgent={this.addAgent}
            />
            <p>
              <L10nHtml id={"signup.agents.invitationsAfterLastStep"} />
            </p>
            <div className={styles.pushdown}>
              <p>
                <L10nHtml id={"signup.password.youEmail"} values={{ email: this.email }} />
              </p>
              <button type={"submit"}>
                <L10n id={"signup.continue"} />
              </button>
            </div>
          </form>
          {this.renderBack()}
        </BeforeLoginSection>
        <BeforeLoginSection styleType={this.styleType}>
          <ProgressDots fulfilledSteps={6} totalSteps={this.totalSteps} />
          <header>
            <h2>
              <L10n id={"signup.payment.addMethod"} />
            </h2>
            <div>
              <p>
                <L10nHtml
                  id={"signup.payment.addMethod.description"}
                  values={{ date: renewDateFormatted }}
                />
              </p>
            </div>
          </header>
          {this.paymentFields && (
            <form onSubmit={this.toPaymentSubmit} id="free-account-payment">
              <div className={styles["payment-form"]}>
                <CountryAutocomplete field={this.paymentFields.country} />
                <AgentInput
                  field={this.paymentFields.companyID}
                  styleType={this.styleType}
                  placeholder={intl.formatMessage("signup.payment.placeholder.VAT")}
                />
              </div>
              <div className={styles.pushdown}>
                <PaymentSummary
                  country={this.paymentFields.country.value?.iso}
                  companyId={this.paymentFields.companyID.$}
                  agentsNumber={
                    this.agentsForm && this.agentsForm.$ ? this.agentsForm.$.length + 1 : 1
                  }
                  planPrice={this.props.price}
                  currency={this.props.currency}
                />
                <p>
                  <L10nHtml id={"signup.password.youEmail"} values={{ email: this.email }} />
                </p>
                <button type="submit" disabled={!this.paymentFields.country.$} role="link">
                  <L10n id={"signup.saveContinue"} />
                </button>
              </div>
            </form>
          )}
          {this.renderBack()}
        </BeforeLoginSection>
      </>
    );
  };

  render() {
    switch (this.state.displayMode) {
      case "completingAccount":
        return this.renderCompletingAccount();
      case "setUp":
        return <SetUpSection />;
      case "error":
        return <ErrorSection />;
      default:
        return <ErrorSection />;
    }
  }
}
